import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, map, take, tap } from 'rxjs';
import { VideoCategoryDTO, VideoDTO } from 'src/app/common/dtos/video.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';

import { deepCopy } from 'src/app/common/utilities/copy.helpers';
import { VideoLibraryFiltersDTO } from '../../dtos/video-library.dto';
import {
  videoCategoryDTOFromAPIResponse,
  videoCreatePayloadFromDTO,
  videoDTOFromAPIResponse,
  videoEditPayloadFromDTO,
  videoModelsFromAPIResponse,
} from '../../helpers/translators/video.translators';
import {
  VideoAPIResponse,
  VideoCategoryResponse,
} from '../../types/responses/video.responses';

@Injectable({
  providedIn: 'root',
})
export class VideoLibraryService {
  // search filters
  private searchFiltersData: VideoLibraryFiltersDTO | null = null;

  private searchFiltersSource = new BehaviorSubject(this.searchFiltersData);

  public searchFiltersObs = this.searchFiltersSource.asObservable();

  // search results
  private searchResultsData: VideoDTO[] | null = null;

  private searchResultsSource = new BehaviorSubject(this.searchResultsData);

  public searchResultsObs = this.searchResultsSource.asObservable();

  // search results meta
  private searchResultsMetaData: PaginationDTO | null = null;

  private searchResultsMetaSource = new BehaviorSubject(
    this.searchResultsMetaData
  );

  public searchResultsMetaObs = this.searchResultsMetaSource.asObservable();

  public editVideo: EventEmitter<string | VideoDTO> = new EventEmitter();

  constructor(private apiService: APICoreService) {}

  setFilters(filters: VideoLibraryFiltersDTO) {
    this.searchFiltersSource.next(filters);
  }

  triggerVideoEditModal(videoId: number) {
    const dto = this.searchResultsSource.value?.find((v) => v.id === videoId);
    if (dto) {
      this.editVideo.emit(dto);
    }
  }

  triggerNewVideoModal() {
    this.editVideo.emit('new');
  }

  fetchVideos(): void {
    let filterFreeze: VideoLibraryFiltersDTO = {};

    if (this.searchFiltersSource?.value) {
      filterFreeze = deepCopy(this.searchFiltersSource.value);
    }

    this.searchResultsSource.next(null);

    this.apiService
      .getRequest('video-library', filterFreeze)
      .pipe(
        map((res) => {
          let videoList: VideoDTO[] = [];
          if (res.items) {
            videoList = videoModelsFromAPIResponse(
              res as { items: VideoAPIResponse[] }
            );
          } else {
            videoList = [];
          }
          // we populate observables, as well as return the videoList.
          this.searchResultsSource.next(videoList);
          this.searchResultsMetaSource.next(res._meta);
          return videoList;
        })
      )
      .subscribe();
  }

  getVideo(videoId: number) {
    return this.apiService
      .getRequest(`video-library/${videoId}`)
      .pipe(map((res) => videoDTOFromAPIResponse(res)));
  }

  getCategories() {
    return this.apiService.getRequest('video-categories').pipe(
      map((res) => {
        const categoriesList: VideoCategoryDTO[] = [];
        if (res.items) {
          res.items.forEach((category: VideoCategoryResponse) => {
            categoriesList.push(videoCategoryDTOFromAPIResponse(category));
          });
        }
        return categoriesList;
      })
    );
  }

  clearFilters() {
    this.setFilters({});
  }

  deleteVimeoVideo(vimeoId: number) {
    return this.apiService.postRequest(`delete-video-vimeo/${vimeoId}`).pipe(
      tap(() => {
        this.fetchVideos();
      })
    );
  }

  deleteVideo(videoId: number) {
    return this.apiService.deleteRequest(`video-delete/${videoId}`).pipe(
      tap(() => {
        this.fetchVideos();
      })
    );
  }

  createVideo(videoData: Partial<VideoDTO>): void {
    const payload = videoCreatePayloadFromDTO(videoData);
    this.apiService
      .postRequest('video-create', payload)
      .pipe(tap(() => this.fetchVideos()))
      .subscribe(take(1));
  }

  updateVideo(videoData: Partial<VideoDTO>): void {
    const payload = videoEditPayloadFromDTO(videoData);
    this.apiService
      .putRequest(`update-video/${videoData.id}`, payload)
      .pipe(tap(() => this.fetchVideos()))
      .subscribe(take(1));
  }
}
